import {Component, Input, OnInit} from '@angular/core';
import {GeneralService} from "../../../../services/generalService";
import {RouteService} from "../../../../services/routeService";
import {NotificationService} from "../../../../services/notificationService";
import {DICTIONARY} from "../../../../dictionary";
import html2pdf from 'html2pdf.js';
import _ from "lodash";
import util from "util";
import {LookAndFeelService} from "../../../../services/lookAndFeelService";

@Component({
  selector: 'threat-simulation-campaign-summary-component',
  templateUrl: './threat-simulation-campaign-summary.component.html',
})
export class ThreatSimulationCampaignSummaryComponent implements OnInit {

	@Input() currentCampaign;
	constructor(public gs:GeneralService,
				private rs:RouteService,
				public lfs:LookAndFeelService,
				private ns:NotificationService) {
	}

	dic = DICTIONARY;
	isTrainingCampaign;
	campaignSummary: any;
	getSummaryInProcess: boolean = false;
	generatePdfInProcess: boolean = false;
	generalEventsChartOptions: any;
	recipientsStatusChartOptions: any;
	quizEventsChartOptions: any;

	// recipient status is split into delivery states (Queued / Not Delivered) and outcome states (Fail / In Progress / Pass / Incomplete).
	// "Pass" is final: it is only assigned once the campaign has ended. before that a delivered recipient with no failure is "In Progress".
	recipientStatusDisplay = {
		'Pass': {icon: 'fa-check-circle', color: 'darkgreen', graphColor: '#2E7D32', tooltip: 'Pass - the recipient completed the campaign without failing'},
		'Fail': {icon: 'fa-exclamation-triangle', color: 'orange', graphColor: '#ff0211',tooltip: 'Fail - the recipient clicked the link, downloaded the attachment or did not pass the quiz'},
		'In Progress': {icon: 'fa-hourglass-half', color: '#3984e5', graphColor: '#1976D2',tooltip: 'In Progress - the email was delivered and the recipient has not responded yet. A final status will be set when the campaign ends'},
		'Queued for Delivery': {icon: 'fa-hourglass-start', color: 'dimgray', graphColor: '#757575',tooltip: 'Queued - the email was not sent yet. Delivery is spread across the campaign dispatch window'},
		'Not Delivered': {icon: 'fa-ban', color: 'red', graphColor: '#c32fd3',tooltip: 'Not Delivered - the email bounced or failed to send'},
		'Incomplete': {icon: 'fa-hourglass-end', color: 'orange', graphColor: '#FFC107',tooltip: 'Incomplete - the campaign ended without the recipient acknowledging the material'}
	};

	ngOnInit() {
		this.isTrainingCampaign = this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.materials.value) ||
			this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.quiz.value);

		this.getSummaryData();
	}

	getSummaryData = () => {
		if (this.currentCampaign.campaigns?.length) {
			const campaignIDs = _.map(this.currentCampaign.campaigns, '_id');
			this.getCampaignGroupSummary(campaignIDs);
		}
		else {
			this.getCampaignSummary();
		}
	}

	getCampaignSummary = () => {
		this.getSummaryInProcess = true;
		this.rs.getCampaignSummary(this.currentCampaign._id).then((response) => {
			this.campaignSummary = response;

			this.prepareGeneralEventsGraph();
			this.prepareRecipientsStatusGraph();
			this.prepareQuizEventsGraph();
			this.getSummaryInProcess = false;
		}, (err) => {
			this.getSummaryInProcess = false;
		});
	}

	getCampaignGroupSummary = (campaignIDs) => {
		this.getSummaryInProcess = true;

		const queryParams = {arr: campaignIDs.join(',')};

		this.rs.getCampaignGroupSummary(queryParams).then((response) => {
			this.campaignSummary = response;

			this.prepareGeneralEventsGraph();
			this.prepareRecipientsStatusGraph();
			this.prepareQuizEventsGraph();
			this.getSummaryInProcess = false;
		}, (err) => {
			this.getSummaryInProcess = false;
		});
	}

	// campaign end = send time + duration. when it cannot be derived (e.g. grouped campaigns without an email record)
	// we fall back to "ended" which preserves the legacy behavior (sent without failure = Pass)
	getCampaignEndTime() {
		const sendTime = this.currentCampaign.email_id?.start_sending_time || this.currentCampaign.email_id?.scheduled_time;
		if (!sendTime) {
			return null;
		}
		const endTime = new Date(sendTime);
		endTime.setDate(endTime.getDate() + (this.currentCampaign.email_id?.advanced?.secure?.expired_days || 7));
		return endTime;
	}

	prepareGeneralEventsGraph() {

		const sentData = {label: 'Sent', value: 0, color: '#02b90b'};
		const readData = {label: 'Read', value: 0, color: '#3984e5'};
		const userReportedData = {label: 'User Reported', value: 0, color: '#e5bd39'};
		const linkClickData = {label: 'Link Clicked', value: 0, color: '#f50909'};
		const attachmentDownload = {label: 'Attachment Downloaded', value: 0, color: '#e76363'};
		const acknowledgeData = {label: 'Acknowledge', value: 0, color: '#2a2929'};
		const quizData = {label: 'Submitted Quiz', value: 0, color: '#2a2929'};
		const phishedLoginData = {label: 'Phished Login', value: 0, color: '#ff00de'};

		this.campaignSummary.recipients?.forEach((recipientObj) => {
			const recipientTracking = {sent: 0, read: 0, userReported: 0, link: 0, attachment: 0, acknowledge: 0, login: 0, quiz: 0};
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventSent.name)) {
				recipientTracking.sent = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventRead.name)) {
				recipientTracking.read = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventTsReport.name)) {
				recipientTracking.read = 1;
				recipientTracking.userReported = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventLinkClicked.name)) {
				recipientTracking.read = 1;
				recipientTracking.link = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventDownloaded.name)) {
				recipientTracking.read = 1;
				recipientTracking.attachment = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventTsLogin.name)) {
				recipientTracking.read = 1;
				recipientTracking.login = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventAcknowledge.name)) {
				recipientTracking.read = 1;
				recipientTracking.acknowledge = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventQuiz.name)) {
				recipientTracking.quiz = 1;
			}

			sentData.value += recipientTracking.sent;
			readData.value += recipientTracking.read;
			userReportedData.value += recipientTracking.userReported;
			linkClickData.value += recipientTracking.link;
			attachmentDownload.value += recipientTracking.attachment;
			acknowledgeData.value += recipientTracking.acknowledge;
			quizData.value += recipientTracking.quiz;
			phishedLoginData.value += recipientTracking.login;
		});

		let graphData = [sentData, readData];

		if (this.isShowAcknowledge(this.currentCampaign)) {
			graphData.push(acknowledgeData);
		}
		if (this.isShowQuizResults(this.currentCampaign)) {
			graphData.push(quizData);
		}

		if (this.isTrainingCampaign) {
			if (!graphData.find(itm => itm.label === linkClickData.label)) {
				graphData.push(linkClickData);
			}
		}
		if (this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.login.value)) {
			if (!graphData.find(itm => itm.label === phishedLoginData.label)) {
				graphData.push(phishedLoginData);
			}
			if (!graphData.find(itm => itm.label === userReportedData.label)) {
				graphData.push(userReportedData);
			}
		}
		if (this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.attachments.value)) {
			if (!graphData.find(itm => itm.label === attachmentDownload.label)) {
				graphData.push(attachmentDownload);
			}
			if (!graphData.find(itm => itm.label === userReportedData.label)) {
				graphData.push(userReportedData);
			}
		}
		if (this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.training.value)) {
			if (!graphData.find(itm => itm.label === linkClickData.label)) {
				graphData.push(linkClickData);
			}
			if (!graphData.find(itm => itm.label === userReportedData.label)) {
				graphData.push(userReportedData);
			}
		}

		graphData.sort((a, b) => b.value - a.value);

		const labels = graphData.map(item => item.label);
		const colors = graphData.map(item => item.color);
		const series = graphData.map(item => item.value);

		const maxValue = series[0];
		const gSeries = series.map((n) => (n / maxValue) * 100.0);

		this.generalEventsChartOptions = {
			maxValue: maxValue,
			series: gSeries,
			chart: {
				height: '115%',
				type: 'radialBar',
				animations: {
					enabled: true,
				},
			},
			plotOptions: {
				radialBar: {
					endAngle: 270,
					hollow: {
						size: '30%',
						background: 'transparent',
					},
					dataLabels: {
						name: {
							show: true,
							fontSize: '11px',
						},
						value: {
							show: true,
							offsetY: 3,
							fontSize: '12px',
							formatter: (seriesValue, opts) => {
								seriesValue = Number(seriesValue);
								for (let i = 0; i < gSeries.length; i++) {
									if (gSeries[i] === seriesValue) {
										seriesValue = series[i];
										break;
									}
								}
								return seriesValue;
							},
						}
					}
				}
			},
			colors: colors,
			labels: labels,
			legend: {
				show: true,
				floating: true,
				fontSize: '12px',
				position: 'left',
				offsetX: 0,
				offsetY: 0,
				labels: {
					useSeriesColors: true,
				},
				markers: {
					size: 8
				},
				formatter: (seriesName, opts) => {
					return `${seriesName}: ${series[opts.seriesIndex]}`;
				},
				onItemHover: {
					highlightDataSeries: false
				},
				itemMargin: {
					vertical: 3
				}
			}
		};
	}

	prepareRecipientsStatusGraph() {
		const series = Object.fromEntries(
			Object.keys(this.recipientStatusDisplay).map(key => [key, 0])
		);

		this.campaignSummary.recipients?.forEach(recipient => {
			if (recipient.status in series) {
				series[recipient.status]++;
			}
		});

		const entries = Object.entries(series)
			.filter(([, count]) => count > 0);

		this.recipientsStatusChartOptions = {
			series: entries.map(([, count]) => count),
			chart: {
				width: '100%',
				type: 'pie'
			},
			labels: entries.map(([status]) => status),
			colors: entries.map(([status]) => this.recipientStatusDisplay[status].graphColor)
		};
	}

	prepareQuizEventsGraph() {
		const passingScore = this.currentCampaign.material?.quiz?.config?.passing_score;
		let didNotSubmit = 0;
		const quizScores = [];

		this.campaignSummary.recipients?.forEach((recipientObj) => {
			const quizEvent = recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventQuiz.name);
			if (quizEvent?.raw_data) {
				quizScores.push(quizEvent.raw_data.score);
			}
			else {
				didNotSubmit++;
			}
		});

		if (passingScore) {
			const passed = quizScores.filter(quizScore => quizScore >= passingScore).length;
			const failed = quizScores.length - passed;
			this.quizEventsChartOptions = {
				series: [didNotSubmit, failed, passed],
				chart: {width: '100%', type: "pie"},
				labels: ["Did not submit", `Failed (below ${passingScore}%)`, `Passed (${passingScore}%+)`],
				colors: ['#7e7e7e', '#f50909', '#02b90b'],
			};
		}
		else {
			const below50 = quizScores.filter(quizScore => quizScore < 50).length;
			const below80 = quizScores.filter(quizScore => quizScore >= 50 && quizScore <= 80).length;
			const above80 = quizScores.filter(quizScore => quizScore > 80).length;
			this.quizEventsChartOptions = {
				series: [didNotSubmit, below50, below80, above80],
				chart: {width: '100%', type: "pie"},
				labels: ["Did not submit", "Below 50%", "50% to 80%", "Above 80%"],
				colors: ['#7e7e7e', '#f50909', '#ffcd00', '#02b90b'],
			};
		}
	}

	isCampaignType = (campaignObj, type) => {
		if (campaignObj.ts_type === type || campaignObj.landing_page_type === type) {
			return true;
		}

		// campaign with custom template (which doesn't have ts_type) will be treated as training campaign
		if (!campaignObj.ts_type && type === this.dic.CONSTANTS.threatSimulationType.training.value) {
			return true;
		}
		//

		if (campaignObj.campaigns?.length) {
			for (let i = 0; i < campaignObj.campaigns.length; i++) {
				if (campaignObj.campaigns[i].ts_type === type || campaignObj.campaigns[i].landing_page_type === type) {
					return true;
				}
				if (!campaignObj.ts_type && type === this.dic.CONSTANTS.threatSimulationType.training.value) {
					return true;
				}
			}
		}

		return false;
	}

	isShowQuizResults = (campaignObj) => {
		if (campaignObj.ts_type === this.dic.CONSTANTS.threatSimulationType.quiz.value || campaignObj.landing_page_type === this.dic.CONSTANTS.threatSimulationType.quiz.value) {
			return true;
		}

		if (campaignObj.campaigns?.length) {
			for (let i = 0; i < campaignObj.campaigns.length; i++) {
				if (campaignObj.campaigns[i].ts_type === this.dic.CONSTANTS.threatSimulationType.quiz.value ||
					campaignObj.campaigns[i].landing_page_type === this.dic.CONSTANTS.threatSimulationLandingPageType.quiz ) {
					return true;
				}
			}
		}

		return false;
	}

	isShowAcknowledge = (campaignObj) => {
		// we do not show "acknowledge" if all campaigns are "training" with "quiz landing pages"
		let areAllLandingPagesQuiz = this.currentCampaign.landing_page_type === this.dic.CONSTANTS.threatSimulationLandingPageType.quiz;
		if (!areAllLandingPagesQuiz && this.currentCampaign.campaigns?.length) {
			areAllLandingPagesQuiz = this.currentCampaign.campaigns.every(campaign => {
				return this.isCampaignType(campaign, this.dic.CONSTANTS.threatSimulationType.training.value) &&
					campaign.landing_page_type === this.dic.CONSTANTS.threatSimulationLandingPageType.quiz;
			});
		}

		return !areAllLandingPagesQuiz &&
			(this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.login.value) ||
			this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.attachments.value) ||
			this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.materials.value) ||
			this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.training.value));
	}

	exportChartsToPdf =  () => {
		if (!this.campaignSummary) {
			return;
		}
		this.generatePdfInProcess = true;

		// set the relevant elements' visual properties to be ideal for the PDF page before copying the HTML
		let areaForPdf:any = document.getElementById('trendsChartsContainer');
		areaForPdf.classList.add('trends-pdf-layout');

		// timeout is needed to let the css enough time to update on screen and for the responsive charts to resize
		setTimeout(() => {
			const element = document.getElementById('trendsChartsContainer');
			const opt = {
				margin:       40,
				filename:     `${this.currentCampaign.name.replace(' ', '_')}_campaign_summary.pdf`,
				image:        { type: 'jpeg' as 'jpeg', quality: 1 },
				html2canvas:  { scale: 2 , width: 1300, windowWidth: 1550},
				jsPDF:        { unit: 'px', format: 'a4', orientation: 'portrait' as 'portrait', hotfixes: ['px_scaling']}
			};

			// New Promise-based usage:
			html2pdf().set(opt).from(element).save().finally(() => {
				setTimeout(() => {
					this.exitFromPdfGeneration();
				})
			});
		},800);
	}

	exitFromPdfGeneration = () => {
		this.ns.showInfoMessage(util.format(this.dic.MESSAGES.downloadSuccess, 'Trends and insights data'));

		document.getElementById('trendsChartsContainer').classList.remove('trends-pdf-layout');
		this.generatePdfInProcess = false;
		// rerender charts so they'll fit their container's size
		this.getSummaryInProcess = true;

		setTimeout(() => {
			this.getSummaryInProcess = false;
		});
	}
}

